import { useCallback } from 'react';

import {
  PayrollReportHistoryRowStatusEnum,
  PayrollReportHistoryRowViewModel,
  ReportingReceiptViewModel,
} from '@fora-monorepo/employer-my-pages-common/types';
import { Box, Table, Tag, TagVariant, Text, useIsDeviceSize } from '@foradevelopment/fora-ui';
import { getReadableTimestamp } from '@fora-monorepo/date-utils';

import { getPayrollReportSummary } from '../../../redux/report/reportThunks';
import { useAppDispatch } from '../../../redux/hooks';
import { ReportHistoryDetail } from './ReportHistoryDetail';
import { ReportModalAndButton } from '../ReportModalAndButton';
import { TypedTFunction, useTypedTranslation } from '../../../i18n/i18n';

type ReportHistoryListTableRowProps = {
  reportHistory: PayrollReportHistoryRowViewModel;
  isInitiallyExpanded?: boolean;
  reportIndex: number;
};

export const ReportHistoryTableRow = ({ reportHistory, reportIndex }: ReportHistoryListTableRowProps) => {
  const { t } = useTypedTranslation();

  const { isMobile, isDesktop } = useIsDeviceSize();
  const dispatch = useAppDispatch();

  const getPayrollReportSummaryById = useCallback(
    async (partnerNumberOrganization: string, reportId: string) => {
      const result = await dispatch(getPayrollReportSummary({ partnerNumberOrganization, reportId }));

      if (getPayrollReportSummary.fulfilled.match(result) && result.payload) {
        return result.payload;
      }
      return Promise.reject('Could not get report details');
    },
    [dispatch]
  );

  const getReportReceiptModalData = async (): Promise<ReportingReceiptViewModel> => {
    const summaryData = await getPayrollReportSummaryById(
      reportHistory.partnerNumberOrganization,
      reportHistory.payrollReportId
    );

    return {
      organizationName: summaryData.organizationName,
      organizationNumber: summaryData.organizationNumber,
      employmentCount: summaryData.employmentCount,
      reportDate: getReadableTimestamp(reportHistory.createdDate),
      reporterName: reportHistory.adminReporterFullName ? reportHistory.adminReporterFullName : '',
      source: reportHistory.source,

      payMonthSummaries:
        summaryData.payMonthSummary?.map(p => ({
          numberOfPays: p.numberOfPays,
          payMonth: p.payMonth,
          totalSum: p.totalSum,
        })) || [],
    };
  };

  const { text, variant } = getStatusTag(t, reportHistory.status);

  return (
    <Table.Row isInitiallyExpanded={false} testId={`table-row-${reportIndex}`}>
      <Table.Cell>
        <Text>{getReadableTimestamp(reportHistory.createdDate)}</Text>
      </Table.Cell>

      {!isMobile && (
        <Table.Cell textAlign={'left'}>{text && variant && <Tag text={text} variant={variant} />}</Table.Cell>
      )}
      <Table.Cell>
        <Text>{reportHistory.employerName}</Text>
      </Table.Cell>
      {!isMobile && (
        <Table.Cell>
          <Text> {reportHistory.adminReporterFullName} </Text>
        </Table.Cell>
      )}
      {isDesktop && (
        <Table.Cell>
          <ReportModalAndButton
            reportIndex={Number(reportHistory.payrollReportId)}
            getReportSummaryModalData={getReportReceiptModalData}
          />
        </Table.Cell>
      )}
      {!isMobile && (
        <Table.Cell>
          {reportHistory.numberOfFiles ? (
            <Text>
              {t('table.reportLog.cellDocument', {
                count: reportHistory.numberOfFiles,
              })}
            </Text>
          ) : (
            <Text color="black70">{t('table.reportLog.cellDocumentEmpty')}</Text>
          )}
        </Table.Cell>
      )}
      <Box key="body">
        <ReportHistoryDetail
          {...reportHistory}
          reportIndex={reportIndex}
          getReportSummaryModalData={getReportReceiptModalData}
        />
      </Box>
    </Table.Row>
  );
};

const getStatusTag = (
  t: TypedTFunction,
  status: PayrollReportHistoryRowStatusEnum
): { text: string | null; variant: TagVariant | null } => {
  switch (status) {
    case PayrollReportHistoryRowStatusEnum.Approved:
      return {
        text: '',
        variant: null,
      };
    case PayrollReportHistoryRowStatusEnum.Processing:
      return {
        text: t('status.reportProcessing.shortname'),
        variant: 'additionalGray',
      };
    case PayrollReportHistoryRowStatusEnum.HasDeviations:
      return {
        text: t('status.reportWithDeviations.shortname'),
        variant: 'error',
      };
  }
};
